import { Basic } from '../basic.ts';
/**
 * @class MSRTSPCamera
 * @extends Basic
 * @classdesc Видеопоток
 */
export class MSRTSPCamera extends Basic {
    constructor() {
        super();
        const shadowRoot = this.attachShadow({ mode: 'open' });
        shadowRoot.innerHTML = `<video controls style="width:100%; height:100%; object-fit:fill;">`;
        this._video = shadowRoot.querySelector('video');
        this.setautoplay(this.getAttribute("autoplay"));

        this.playCallback = (event) => {
            this.setautoplay(true);
        }
        this._video.addEventListener("play", this.playCallback);

        this.pauseCallback = (event) => {
            this.setautoplay(false);
        }
        this._video.addEventListener("pause", this.pauseCallback);

        this.afterSwitchBind = this.afterSwitch.bind(this);
        window.addEventListener("ms_message", this.afterSwitchBind);
    }

    static get observedAttributes() {
        return super.observedAttributes.concat([
            'autoplay',
            'url',
            'username',
            'password',
            'iceservers',
        ]);
    }

    get autoplay() {
        return this._autoplay;
    }
    set autoplay(value) {
        this.setautoplay(value);
    }

    /**
     * Имя ресурса для изображения фона
     * @type {string}
     */
    get resource() {
        return this._resource;
    }
    set resource(value) {
        this._resource = this.getResourceFromList(value);
        this._video.autoplay = this._autoplay;
        this._video.src = `resources/${this._resource}`;
        this._video.load();
        this.checkforVideo();
    }

    // URL-адрес видеокамеры (RTSP ссылка)
    get url() {
        return this._url;
    }
    set url(value) {
        this._url = value;

        switch (true) {
            case value.toLowerCase().startsWith('rtsp'): {
                this._streamProtocol = 'RTSP';
                this._url = value;
                break;
            }
            default: {
                this._streamProtocol = 'WEBRTC';
                this._url = value;
                break;
            }
        }

        if (this.isControlConnected) {
            this.connectToCamera();
        }
    }

    // userName для подключения к видеокамере
    get username() {
        return this._username;
    }
    set username(value) {
        this._username = value;
        if (this.isControlConnected) {
            this.connectToCamera();
        }
    }

    // пароль для подключения к видеокамере
    get password() {
        return this._password;
    }
    set password(value) {
        this._password = value;
        if (this.isControlConnected) {
            this.connectToCamera();
        }
    }

    // Ice серверы
    get iceservers() {
        return this._iceservers ?? "";
    }
    set iceservers(value) {
        this._iceservers = JSON.parse(value).iceserver;
        if (this.isControlConnected) {
            this.connectToCamera();
        }
    }

    setautoplay(value) {
        this._autoplay = this._toBool(value);
        this._video.autoplay = this._autoplay;
        this.checkforVideo();
    }

    checkforVideo() {
        if(this.checkforVideoInterval){
            clearInterval(this.checkforVideoInterval)
        }
        //Every 500ms, check if the video element has loaded
        this.checkforVideoInterval = setInterval(() => {
            if (this._video && this._video.readyState >= 3) {
                //This block of code is triggered when the video is loaded

                //your code goes here
                if (this.autoplay) {
                    this._video.muted = true;
                    this._video.play();
                } else {
                    this._video.pause();
                }

                //stop checking every half second
                clearInterval(this.checkforVideoInterval);
            }
        }, 500);
    }

    async connectToCamera() {
        if (!this.url) {
            return;
        }
        this.cleanConnection();

        switch (this._streamProtocol) {
            case 'WEBRTC':
                await this.connectToWebRTC();
                break;
            case 'RTSP':
            default:
                await this.connectToRTSPViaWebRTC();
                break;
        }
    }

    async connectToWebRTC() {
        // напрямую подключаемся по WebRTC
        this.pc = new RTCPeerConnection({
            iceServers: this.buildIceServers()
        });

        this.pc.addTransceiver('video', { direction: 'recvonly' });
        this.pc.addTransceiver('audio', { direction: 'recvonly' });

        this.pc.ontrack = ({ track, streams: [stream] }) => {
            track.onunmute = () => {
                this._video.srcObject = stream;
            };
        };

        const offer = await this.pc.createOffer();
        await this.pc.setLocalDescription(offer);

        // нужно, чтобы убрать ошибку 404 на PATCH, а вообще правильнее будет реализовывать через onicecandidate
        await new Promise((resolve) => {
            if (this.pc.iceGatheringState === 'complete') {
                resolve();
            } else {
                const listener = () => {
                    if (this.pc.iceGatheringState === 'complete') {
                        this.pc.removeEventListener('icegatheringstatechange', listener);
                        resolve();
                    }
                };
                this.pc.addEventListener('icegatheringstatechange', listener);
            }
        });

        const whepUrl = new URL(this._url);
        whepUrl.pathname = `${whepUrl.pathname}whep`;

        const offerResponse = await fetch(whepUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/sdp' },
            body: this.pc.localDescription.sdp,
        });

        if (!offerResponse.ok) {
            const errorText = await offerResponse.text();
            throw new Error(`Ошибка при отправке SDP-offer: ${offerResponse.status} - ${errorText}`);
        }

        const answerSdp = await offerResponse.text();
        await this.pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });

        const remoteCandidates = await this.parseCandidatesFromSdp(answerSdp);
        for (const candidate of remoteCandidates) {
            await this.pc.addIceCandidate(candidate);
        }
    }

    buildIceServers() {
        if (!this.iceservers) {
            return [];
        }

        if (!Array.isArray(this.iceservers)) {
            const { url, username, credential } = this.iceservers;
            return [{ urls: url, username, credential }]
        }

        return this.iceservers.map(({ url, username, credential }) => ({
            urls: url,
            username,
            credential
        }));
    }

    async parseCandidatesFromSdp(sdp) {
        const candidates = [];
        const lines = sdp.split('\r\n');
        let sdpMLineIndex = null;
        let sdpMid = null;
        let mLineCounter = 0;

        for (const line of lines) {
            if (line.startsWith('m=')) {
                const parts = line.split(' ');
                if (parts.length >= 3) {
                    sdpMLineIndex = mLineCounter;
                    const mediaType = parts[0].substring(2);
                    if (mediaType === 'video') {
                        sdpMid = '0';
                    } else if (mediaType === 'audio') {
                        sdpMid = '1';
                    } else {
                        sdpMid = null;
                    }
                    mLineCounter++;
                }
            } else if (line.startsWith('a=candidate:')) {
                if (sdpMLineIndex !== null && sdpMid !== null) {
                    candidates.push({
                        candidate: line.substring(2),
                        sdpMid,
                        sdpMLineIndex
                    });
                }
            }
        }
        return candidates;
    }

    async connectToRTSPViaWebRTC() {
        // подключаемся по WebRTC через наш сервер на c#
        const cameraConfiguration = {
            url: this.url,
            userName: this.username,
            password: this.password,
            id: `${window.crypto.getRandomValues(new Uint32Array(1))}` // uniqueId, без него на стороне c# будут падать ошибки
        };

        const baseUrl = $sw.serverState.haveReserv
            ? `//${$sw.serverState.location.hostname}:${window.location.port}/rtsptowebrtcapi/`
            : `//${window.location.hostname}:${window.location.port}/rtsptowebrtcapi/`;

        this.pc = new RTCPeerConnection();

        this.pc.ontrack = ({ track, streams: [stream] }) => {
            track.onunmute = () => {
                this._video.srcObject = stream;
            };
        };

        this.pc.onicecandidate = async (event) => {
            if (event.candidate) {
                await fetch(`${baseUrl}add-ice-candidate`, {
                    method: 'POST',
                    body: JSON.stringify({
                        id: cameraConfiguration.id,
                        iceCandidate: event.candidate
                    }),
                    headers: { 'Content-Type': 'application/json' }
                });
            }
        };

        const offerResponse = await fetch(`${baseUrl}get-or-add-camera-stream`, {
            method: 'POST',
            body: JSON.stringify(cameraConfiguration),
            headers: { 'Content-Type': 'application/json' }
        });
        const offer = await offerResponse.json();
        await this.pc.setRemoteDescription(offer);
        const answer = await this.pc.createAnswer();
        await this.pc.setLocalDescription(answer);
        await fetch(`${baseUrl}set-answer`, {
            method: 'POST',
            body: JSON.stringify({
                id: cameraConfiguration.id,
                answer: this.pc.localDescription
            }),
            headers: { 'Content-Type': 'application/json' }
        });
    }

    cleanConnection() {
        if (this.pc) {
            this.pc.ontrack = null;
            this.pc.onicecandidate = null;
            this.pc.close();
            this.pc = null;
        }

        if (this._video) {
            this._video.srcObject = null;
            this._video.src = "";
        }
    }

    afterSwitch(message) {
        const data = message.data;
        switch (data.event) {
            case "serverChanged":
                this.connectToCamera();
                break;
        }
    }

    connectedCallback() {
        // Обсудили, что нужно делать 1 вызов `get-or-add-camera-stream` после инициализации контрола и далее уже переподключаться из сеттеров,
        // если в них приходят изменения.
        // Если вызывать `get-or-add-camera-stream` 3 раза наподряд до инициализации контрола, то на сервере начинают падать ошибки.
        this.isControlConnected = true;
        this.connectToCamera();
    }

    disconnectedCallback() {
        this.cleanConnection();
        this._video.removeEventListener("play", this.playCallback);
        this._video.removeEventListener("pause", this.pauseCallback);
        window.removeEventListener("ms_message", this.afterSwitchBind);
        clearInterval(this.checkforVideoInterval);
        super.disconnectedCallback();
    }
}